<template>
  <NuxtLink
    :to="ctaLink"
    class="pp-brief"
  >
    <div class="thumb">
      <div
        v-if="thumbnail"
        class="thumb-img"
        :style="{ backgroundImage: `url(${thumbnail})` }"
      ></div>
      <div
        v-else
        class="thumb-placeholder"
        aria-hidden="true"
      ></div>
      <div class="play">
        <div class="btn">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      <div
        v-if="durationLabel"
        class="dur"
      >
        {{ durationLabel }}
      </div>
    </div>
    <div class="body">
      <div class="eyebrow">
        <span class="new">{{ t('property.developerBriefing.badge') }}</span>
        <span v-if="developerName">{{ t('property.developerBriefing.briefingLabel', { developerName }) }}</span>
      </div>
      <h3>{{ title }}</h3>
      <p
        v-if="description"
        class="desc"
      >
        {{ description }}
      </p>
      <div
        v-if="metaItems.length"
        class="meta"
      >
        <template
          v-for="(meta, idx) in metaItems"
          :key="idx"
        >
          <span
            v-if="idx > 0"
            class="sep"
            >·</span
          >
          <span>{{ meta }}</span>
        </template>
      </div>
    </div>
    <span class="cta">
      {{ t('property.developerBriefing.watchBriefing') }}
      <span class="rtl:inline-block rtl:-scale-x-100 arr">→</span>
    </span>
  </NuxtLink>
</template>

<script setup lang="ts">
defineProps<{
  title: string;
  description?: string;
  developerName?: string;
  thumbnail?: string | null;
  durationLabel?: string | null;
  metaItems: string[];
  ctaLink: string;
}>();

const { t } = useI18n();
</script>

<style scoped lang="scss">
.pp-brief {
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 22px;
  align-items: center;
  padding-block: 18px;
  padding-inline: 18px 22px;
  margin-bottom: 18px;
  background: linear-gradient(100deg, #0d0f15 0%, #1a1c24 48%, #2f3138 100%);
  border-radius: 18px;
  color: white;
  position: relative;
  overflow: hidden;
  transition: transform 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    inset-inline-end: 0;
    bottom: 0;
    width: 45%;
    background: radial-gradient(ellipse at 100% 50%, rgba(255, 182, 72, 0.22), transparent 60%);
    pointer-events: none;
  }

  @media (max-width: 744px) {
    grid-template-columns: 1fr;
    gap: 14px;
  }
}

.thumb {
  position: relative;
  width: 200px;
  height: 118px;
  border-radius: 12px;
  overflow: hidden;
  background: #1a1c24;

  @media (max-width: 744px) {
    width: 100%;
  }
}

.thumb-img {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
}

.thumb-placeholder {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #2a2118, #0d0f15);
}

.play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  .btn {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: #ffb648;
    color: #0d0f15;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 6px 18px rgba(255, 182, 72, 0.35);
  }
}

.dur {
  position: absolute;
  bottom: 8px;
  inset-inline-end: 8px;
  padding: 3px 7px;
  border-radius: 5px;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 700;
}

.body {
  min-width: 0;
  position: relative;
  z-index: 1;

  .eyebrow {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #ffb648;
    font-weight: 700;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;

    .new {
      padding: 2px 7px;
      border-radius: 5px;
      background: #ffb648;
      color: #0d0f15;
      font-weight: 800;
      letter-spacing: 0.08em;
    }
  }

  h3 {
    font-family: 'Fraunces', 'Times New Roman', serif;
    font-weight: 600;
    font-size: 22px;
    letter-spacing: -0.015em;
    line-height: 1.2;
    color: white;
    margin: 0 0 6px;
  }

  .desc {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.45;
    margin: 0;
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);

    .sep {
      color: rgba(255, 255, 255, 0.25);
    }
  }
}

.cta {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  padding: 0 20px;
  border-radius: 12px;
  background: #ffb648;
  color: #0d0f15;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;

  &:hover {
    background: #eda436;
  }

  .arr {
    font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-weight: 400;
  }
}
</style>
